import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Grid2,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import LockResetIcon from "@mui/icons-material/LockReset";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";

import PageHeader from "../components/common/PageHeader";
import SearchFilters from "../components/common/SearchFilters";
import userService, { User, PaginationParams } from "../services/userService";
import SearchButton from "../components/common/Filters/SearchButton";
import FilterGrid2 from "../components/common/Filters/FilterGrid2";
import CommonPagination from "../components/common/CommonPagination";
import AddButton from "../components/common/Filters/AddButton";
import SingleSelectFilter from "../components/common/Filters/SingleSelectFilter";
import SearchInput from "../components/common/Filters/SearchInput";

const Users: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [filter, setFilter] = useState<any>({
    roles: ['admin', 'user'],
  });
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [roleFilter, setRoleFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [paginationModel, setPaginationModel] = useState<PaginationParams>({
    page: 0,
    perPage: isMobile ? 5 : 10,
  });
  const [totalUsers, setTotalUsers] = useState<number>(0);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params: PaginationParams = {
        page: paginationModel.page + 1, // API uses 1-based indexing
        perPage: paginationModel.perPage,
        // search: searchTerm || undefined,
        ...filter,
        // roles: ['user']
        // roles: roleFilter ? [roleFilter] : undefined // เพิ่ม filter เฉพาะ role admin และ user
      };

      const { users: fetchedUsers, pagination } = await userService.getUsers(
        params
      );
      setUsers(fetchedUsers);
      setTotalUsers(pagination.total);
      setError(null);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("ไม่สามารถโหลดข้อมูลผู้ใช้งานได้ กรุณาลองใหม่อีกครั้ง");
      setSnackbar({
        open: true,
        message: "ไม่สามารถโหลดข้อมูลผู้ใช้งานได้ กรุณาลองใหม่อีกครั้ง",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleAddUser = () => {
    navigate("/users/add");
  };

  const handleEditUser = (username: string) => {
    navigate(`/users/${username}/edit`, { state: { from: 'users' } });
  };

  const handleResetPasswordClick = (user: User) => {
    navigate(`/users/${user.username}/change-password`);
  };

  const handleDeleteClick = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;

    try {
      await userService.deleteUser(selectedUser.username);
      setSnackbar({
        open: true,
        message: "ลบผู้ใช้งานสำเร็จ",
        severity: "success",
      });
      fetchUsers(); // Refresh the user list
    } catch (error) {
      console.error("Error deleting user:", error);
      setSnackbar({
        open: true,
        message: "เกิดข้อผิดพลาดในการลบผู้ใช้งาน",
        severity: "error",
      });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedUser(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  const handleSearch = () => {
    // setSearchTerm(searchValue);
    setFilter({ search: searchTerm, roles: roleFilter ? [roleFilter] : ['admin', 'user'], status: statusFilter});
    setPaginationModel((prev) => ({ ...prev, page: 0 })); // Reset to first page on new search
  };

  const handleFilterChange = (filterName: string, value: string) => {
    if (filterName === "role") {
      setRoleFilter(value);
    } else if (filterName === "status") {
      setStatusFilter(value);
    }
    setPaginationModel((prev) => ({ ...prev, page: 0 })); // Reset to first page on filter change
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const searchFilters = [
    {
      label: "ประเภทผู้ใช้งาน",
      value: "role",
      options: [
        { value: "", label: "ทั้งหมด" },
        { value: "admin", label: "ผู้ดูแลระบบ" },
        { value: "user", label: "ผู้ใช้งาน" },
      ],
    },
    {
      label: "สถานะการใช้งาน",
      value: "status",
      options: [
        { value: "", label: "ทั้งหมด" },
        { value: "active", label: "ใช้งาน" },
        { value: "inactive", label: "ไม่ใช้งาน" },
      ],
    },
  ];

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: "100%",
      }}
    >
      <PageHeader title="จัดการผู้ใช้งาน" />

      <FilterGrid2>
        <Grid2 size={{ xs: 12, sm: 3 }}>
          <SearchInput
            title="ค้นหาผู้ใช้งาน"
            value={searchTerm}
            autoChange
            placeholder="ค้นหาผู้ใช้งาน"
            onChange={(value) => setSearchTerm(value)}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 4, md: 2 }}>
          <SingleSelectFilter
            filter={{
              label: "ประเภทผู้ใช้งาน",
              value: "role",
              options: [
                { value: "admin", label: "ผู้ดูแลระบบ" },
                { value: "user", label: "ผู้ใช้งาน" },
              ],
            }}
            value={roleFilter}
            onChange={(value) => setRoleFilter(value.target.value as string)}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 4, md: 2 }}>
          <SingleSelectFilter
            filter={{
              label: "สถานะการใช้งาน",
              value: "status",
              options: [
                { value: "active", label: "ใช้งาน" },
                { value: "inactive", label: "ไม่ใช้งาน" },
              ],
            }}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value.target.value as string)}
          />
        </Grid2>
        <Grid2>
          <SearchButton onClick={() => handleSearch()} />
        </Grid2>
        <Grid2 sx={{ marginLeft: { xs: 0, md: 0, lg: "auto" } }}>
          <AddButton onClick={handleAddUser} label="เพิ่มผู้ใช้งาน" />
        </Grid2>
      </FilterGrid2>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center" width={70}>
                  ลำดับ
                </TableCell>
                <TableCell align="center" width={isMobile ? 200 : 250}>
                  ชื่อ-นามสกุล
                </TableCell>
                <TableCell align="center" width={isMobile ? 200 : 250}>
                  Username
                </TableCell>
                <TableCell align="center" width={isMobile ? 120 : 150}>
                  ประเภทผู้ใช้งาน
                </TableCell>
                <TableCell align="center" width={isMobile ? 120 : 150}>
                  เบอร์โทร
                </TableCell>
                <TableCell align="center" width={isMobile ? 120 : 150}>
                  สถานะการใช้งาน
                </TableCell>
                <TableCell align="center" width={isMobile ? 150 : 180}>
                  จัดการ
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <Typography variant="h6" color="text.secondary">
                      ไม่พบข้อมูล
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user, index) => (
                  <TableRow key={user._id}>
                    <TableCell align="center">
                      {paginationModel.page * paginationModel.perPage +
                        index +
                        1}
                    </TableCell>
                    <TableCell align="center">{user.name}</TableCell>
                    <TableCell align="center">{user.username}</TableCell>
                    <TableCell align="center">
                      {user.roles
                        ?.sort((a, b) => {
                          const order: Record<string, number> = { 'admin': 1, 'user': 2, 'employee': 3 };
                          return (order[a.toLowerCase()] || 999) - (order[b.toLowerCase()] || 999);
                        })
                        ?.map((role) => {
                          switch (role.toLowerCase()) {
                            case "admin":
                              return "ผู้ดูแลระบบ";
                            case "user":
                              return "ผู้ใช้งาน";
                            case "employee":
                              return "พนักงาน";
                            default:
                              return role;
                          }
                        })
                        .join(", ")}
                    </TableCell>
                    <TableCell align="center">{user.phoneNumber}</TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          color: user.isEnable ? "success.main" : "error.main",
                          fontWeight: 500,
                          fontSize: isMobile ? "0.875rem" : "1rem",
                        }}
                      >
                        {user.isEnable ? "ใช้งาน" : "ไม่ใช้งาน"}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <IconButton
                          onClick={() => handleEditUser(user.username)}
                        >
                          <Box
                            component="img"
                            src="/images/icons/button-modify.png"
                            alt="Edit"
                            sx={{ width: 32, height: 32 }}
                          />
                        </IconButton>
                        <IconButton
                          onClick={() => handleResetPasswordClick(user)}
                          sx={{
                            color: "warning.main",
                            "&:hover": { color: "warning.dark" },
                          }}
                        >
                          <LockResetIcon
                            fontSize={isMobile ? "small" : "medium"}
                          />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteClick(user)}>
                          <Box
                            component="img"
                            src="/images/icons/button-bin.png"
                            alt="Delete"
                            sx={{ width: 32, height: 32 }}
                          />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <CommonPagination
          count={Math.ceil(totalUsers / paginationModel.perPage)}
          page={paginationModel.page + 1}
          onChange={(event, value) => {
            setPaginationModel((prev) => ({
              ...prev,
              page: value - 1,
            }));
          }}
          rowsPerPage={paginationModel.perPage}
          onRowsPerPageChange={(value) => {
            setPaginationModel((prev) => ({
              ...prev,
              perPage: value,
              page: 0,
            }));
          }}
          totalItems={totalUsers}
        />
      </Paper>

      {/* Dialog สำหรับยืนยันการลบผู้ใช้งาน */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          ท่านต้องการยืนยันการลบผู้ใช้งาน ใช่หรือไม่ ?
          <IconButton
            aria-label="close"
            onClick={handleDeleteCancel}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography variant="body1">
            คุณต้องการลบผู้ใช้งาน "{selectedUser?.name}" (
            {selectedUser?.username}) ใช่หรือไม่?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            ยกเลิก
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="primary"
            variant="contained"
          >
            ยืนยัน
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Users;
